/* eslint-disable react/display-name */
import { Box, Flex, Link, Skeleton } from '@chakra-ui/react';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import Fuse from 'fuse.js';
import * as _ from 'lodash';
import React, { useMemo, useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import {
  useGetAggregatedLimitedPartnerCommitmentDataForLenderQuery,
  useGetAggregatedLimitedPartnerCommitmentDataForLenderPgQuery,
  AggregatedLimitedPartnerCommitmentDataGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { LimitedPartnerFiltersFormState } from './LimitedPartnerFiltersDrawer';
import { SearchBarInput } from 'packages/common-frontend/components/SearchAndCopyComponents';
import { TableBETA } from 'packages/common-frontend/tables/TableBETA';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { remToPx } from '~/packages/common-frontend/theme/utils';

const TableSearch = ({
  updateLimitedPartnerNameFilter,
}: {
  updateLimitedPartnerNameFilter: (name: string) => void;
}) => {
  const [query, setQuery] = useState('');

  const updateTableFilter = useMemo(
    () =>
      _.debounce((q: string) => {
        updateLimitedPartnerNameFilter(q);
      }, 300),
    [updateLimitedPartnerNameFilter],
  );

  useEffect(() => {
    updateTableFilter(query);
  }, [query, updateTableFilter]);

  return (
    <SearchBarInput
      query={query}
      onChangeQuery={setQuery}
      inputProps={{
        placeholder: 'Search to filter table results',
        width: '100%',
      }}
    />
  );
};

export const AggregatedLimitedPartnerCommitmentsTable = ({
  aggregatedLimitedPartnerCommitments,
}: {
  aggregatedLimitedPartnerCommitments: AggregatedLimitedPartnerCommitmentDataGQLType[];
}) => {
  const columns = useMemo(() => {
    const tableColumns: ColumnDef<
      AggregatedLimitedPartnerCommitmentDataGQLType,
      any
    >[] = [
      {
        accessorKey: 'limitedPartnerName',
        header: 'Name',
        minSize: remToPx(10),
        size: remToPx(10),
        cell: ({ row }) => (
          <>
            {row.original.limitedPartnerName && row.original.limitedPartnerId && (
              <Link
                color="blue.400"
                to={`/limited-partners/details/${row.original.limitedPartnerId}`}
                as={RouterLink}
                _hover={{
                  textDecoration: 'underline',
                }}
                style={{ wordBreak: 'break-word' }}
              >
                {row.original.limitedPartnerName}
              </Link>
            )}
          </>
        ),
      },
      {
        accessorKey: 'commitmentCount',
        minSize: remToPx(10),
        size: remToPx(10),
        header: 'Number of LP Commitments',
      },
      {
        accessorKey: 'totalCommittedCapital',
        minSize: remToPx(10),
        size: remToPx(10),
        header: 'Total Committed Capital',
        cell: ({ row }) =>
          commonUtils.numberToCurrencyWithSymbol({
            number: row.original.totalCommittedCapital,
          }),
      },
      {
        accessorKey: 'percentageOfPortfolioCommittedCapital',
        minSize: remToPx(11),
        size: remToPx(11),
        header: '% of Portfolio Committed Capital',
        cell: ({ row }) =>
          commonUtils.numberToFormattedString(
            row.original.percentageOfPortfolioCommittedCapital,
            'percentage',
            false,
          ),
      },
      {
        accessorKey: 'totalUncalledCapital',
        header: 'Uncalled Capital',
        minSize: remToPx(10),
        size: remToPx(10),
        cell: ({ row }) =>
          commonUtils.numberToCurrencyWithSymbol({
            number: row.original.totalUncalledCapital,
          }),
      },
      {
        accessorKey: 'percentageOfPortfolioUncalledCapital',
        minSize: remToPx(10),
        size: remToPx(10),
        header: '% of Portfolio Uncalled Capital',
        cell: ({ row }) =>
          commonUtils.numberToFormattedString(
            row.original.percentageOfPortfolioUncalledCapital,
            'percentage',
            false,
          ),
      },
      {
        accessorKey: 'totalRecallableCapital',
        header: 'Recallable Capital',
        minSize: remToPx(10),
        size: remToPx(10),
        cell: ({ row }) =>
          commonUtils.numberToCurrencyWithSymbol({
            number: row.original.totalRecallableCapital,
          }),
      },
      {
        accessorKey: 'eligibleUncalledCapital',
        minSize: remToPx(10),
        size: remToPx(10),
        header: 'Eligible Uncalled Capital',
        cell: ({ row }) =>
          commonUtils.numberToCurrencyWithSymbol({
            number: row.original.eligibleUncalledCapital,
          }),
      },
      {
        accessorKey: 'eligibleUncalledCapitalLessKickouts',
        minSize: remToPx(13),
        size: remToPx(13),
        header: 'Eligible Uncalled Capital Less Kickouts',
        cell: ({ row }) =>
          commonUtils.numberToCurrencyWithSymbol({
            number: row.original.eligibleUncalledCapitalLessKickouts,
          }),
      },
      {
        accessorKey: 'advanceRate',
        minSize: remToPx(10),
        size: remToPx(10),
        header: 'Advance Rate',
        cell: ({ row }) =>
          commonUtils.numberToFormattedString(
            row.original.advanceRate,
            'percentage',
            false,
          ),
      },
      {
        accessorKey: 'netUncalledCapital',
        minSize: remToPx(10),
        size: remToPx(10),
        header: 'Net Uncalled Capital',
        cell: ({ row }) =>
          commonUtils.numberToCurrencyWithSymbol({
            number: row.original.netUncalledCapital,
          }),
      },
      {
        accessorKey: 'percentageOfPortfolioNetUncalledCapital',
        minSize: remToPx(10),
        size: remToPx(10),
        header: '% of Portfolio Net Uncalled Capital',
        cell: ({ row }) =>
          commonUtils.numberToFormattedString(
            row.original.percentageOfPortfolioNetUncalledCapital,
            'percentage',
            false,
          ),
      },
    ];

    return tableColumns;
  }, []);

  const [limitedPartnerNameQuery, setLimitedPartnerNameQuery] = useState('');

  const fuse = useMemo(() => {
    return new Fuse(aggregatedLimitedPartnerCommitments, {
      keys: ['limitedPartnerName'],
      threshold: 0.5,
    });
  }, [aggregatedLimitedPartnerCommitments]);

  const filteredData = useMemo(() => {
    if (!limitedPartnerNameQuery) return aggregatedLimitedPartnerCommitments;
    return fuse.search(limitedPartnerNameQuery).map((res) => res.item);
  }, [aggregatedLimitedPartnerCommitments, fuse, limitedPartnerNameQuery]);

  const table = useReactTable({
    data: filteredData,
    columns,
    initialState: {
      columnPinning: { left: ['limitedPartnerName'] },
    },
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
    getRowId: (row) => row.limitedPartnerId,
  });

  return (
    <Flex gap="1rem" direction="column">
      <TableSearch
        updateLimitedPartnerNameFilter={setLimitedPartnerNameQuery}
      />
      <TableBETA table={table} FlexContainerProps={{ maxHeight: '60vh' }} />
    </Flex>
  );
};

export const AggregatedLimitedPartnerCommitmentsTableContainer = ({
  activeFilters,
  usePg,
}: {
  activeFilters?: LimitedPartnerFiltersFormState;
  usePg?: boolean;
}) => {
  const { user } = useUserAuthOrThrow();

  const useQueryHook = usePg
    ? useGetAggregatedLimitedPartnerCommitmentDataForLenderPgQuery
    : useGetAggregatedLimitedPartnerCommitmentDataForLenderQuery;

  const {
    data: aggregatedCommitmentsData,
    error: aggregatedCommitmentsDataError,
  } = useQueryHook({
    variables: {
      lenderOrganizationId: user.organizationId,
      filters: {
        committedCapitalRange: activeFilters?.committedCapitalRange || {},
        uncalledCapitalRange: activeFilters?.uncalledCapitalRange || {},
        netUncalledCapitalRange: activeFilters?.netUncalledCapitalRange || {},
      },
    },
  });
  if (aggregatedCommitmentsDataError) {
    throw aggregatedCommitmentsDataError;
  }
  return (
    <Box mt="2.5rem">
      {aggregatedCommitmentsData ? (
        <AggregatedLimitedPartnerCommitmentsTable
          aggregatedLimitedPartnerCommitments={
            aggregatedCommitmentsData.aggregatedLimitedPartnerCommitmentData
          }
        />
      ) : (
        <Skeleton height="60vh" />
      )}
    </Box>
  );
};
